/**
 * ============================================================================
 * TASK ESTIMATION SERVICE - PHASE 4 COMPLIANT
 * ============================================================================
 *
 * Facade coordinating focused task-estimation sub-services.
 *
 * @phase 4
 * @module services/features/task-estimation
 */

import { IService, ServiceResponse } from '@/services/core/service-registry';
import { logger } from '@/lib/logger';
import type {
  CreateTaskEstimationRequest,
  EstimationFactor,
  TaskEstimation,
  UpdateTaskEstimationRequest,
} from "@/types/time-management";
import { TaskEstimationCrudService } from './task-estimation-crud.service';
import { TaskEstimationHistoricalService } from './task-estimation-historical.service';
import { TaskEstimationAIService } from './task-estimation-ai.service';
import { TaskEstimationValidationService } from './task-estimation-validation.service';

export { TaskEstimationCrudService } from './task-estimation-crud.service';
export { TaskEstimationHistoricalService } from './task-estimation-historical.service';
export { TaskEstimationAIService } from './task-estimation-ai.service';
export { TaskEstimationValidationService } from './task-estimation-validation.service';

/**
 * Minimal facade interface for task estimation.
 * Sub-services can be imported directly for advanced operations.
 */
export interface ITaskEstimationService extends IService {
  getTaskEstimations(taskId: string): Promise<ServiceResponse<TaskEstimation[]>>;
  createEstimation(request: CreateTaskEstimationRequest): Promise<ServiceResponse<TaskEstimation>>;
  updateEstimation(
    estimationId: string,
    updates: UpdateTaskEstimationRequest
  ): Promise<ServiceResponse<TaskEstimation>>;
  generateAIEstimation(
    taskTitle: string,
    description?: string,
    category?: string,
    userId?: string
  ): Promise<
    ServiceResponse<{
      estimatedDuration: number;
      confidenceLevel: "low" | "medium" | "high";
      factors: EstimationFactor[];
      reasoning: string;
    }>
  >;
}

/**
 * Facade for task estimation services.
 * Delegates to focused sub-services for CRUD, AI estimation, historical analysis, and validation.
 */
export class TaskEstimationService implements ITaskEstimationService {
  public readonly name = "TaskEstimationService";
  private initialized = false;
  private crudService: TaskEstimationCrudService;
  private historicalService: TaskEstimationHistoricalService;
  private aiService: TaskEstimationAIService;
  private validationService: TaskEstimationValidationService;

  constructor() {
    this.crudService = new TaskEstimationCrudService();
    this.historicalService = new TaskEstimationHistoricalService();
    this.aiService = new TaskEstimationAIService(
      this.crudService,
      this.historicalService
    );
    this.validationService = new TaskEstimationValidationService(this.crudService);
  }

  async initialize(): Promise<void> {
    if (this.initialized) {return;}

    try {
      logger.info("📊 Initializing Task Estimation Service (Phase 4)...");
      await this.crudService.initialize();
      await this.historicalService.initialize();
      await this.aiService.initialize();
      await this.validationService.initialize();
      this.initialized = true;
      logger.info("✅ Task Estimation Service initialized");
    } catch (error) {
      logger.error("❌ Task Estimation Service initialization failed:", { error: error });
      throw error;
    }
  }

  async dispose(): Promise<void> {
    this.initialized = false;
    await this.crudService.dispose();
    await this.historicalService.dispose();
    await this.aiService.dispose();
    await this.validationService.dispose();
    logger.info("🔌 Task Estimation Service disposed");
  }

  async getTaskEstimations(taskId: string): Promise<ServiceResponse<TaskEstimation[]>> {
    return this.crudService.getTaskEstimations(taskId);
  }

  async createEstimation(request: CreateTaskEstimationRequest): Promise<ServiceResponse<TaskEstimation>> {
    return this.crudService.createEstimation(request);
  }

  async updateEstimation(
    estimationId: string,
    updates: UpdateTaskEstimationRequest
  ): Promise<ServiceResponse<TaskEstimation>> {
    return this.crudService.updateEstimation(estimationId, updates);
  }

  async generateAIEstimation(
    taskTitle: string,
    description?: string,
    category?: string,
    userId?: string
  ): Promise<
    ServiceResponse<{
      estimatedDuration: number;
      confidenceLevel: "low" | "medium" | "high";
      factors: EstimationFactor[];
      reasoning: string;
    }>
  > {
    return this.aiService.generateAIEstimation(taskTitle, description, category, userId);
  }
}

export const taskEstimationService = new TaskEstimationService();
export { taskEstimationService as default };
